import { useEffect, useMemo, useState } from "react"
import { Check, Clock, Moon } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { useTheme } from "../../state/theme"
import { ACCENT_KEYS } from "../../design/palette"
import {
	Avatar,
	Button,
	Card,
	ColourPicker,
	cx,
	Field,
	Input,
	SectionHeading,
	Select
} from "../../components/ui"
import { fullTimestamp, zoneLabel } from "../../lib/format"

export function ProfileSettings() {
	const { user, config, refreshUser, toast } = useApp()
	// "UTC" rather than "" - the live clock below renders on the first pass,
	// before the profile has arrived, and an empty zone is not a valid one.
	const [form, setForm] = useState({
		name: "",
		role: "",
		company: "",
		colour: "teal",
		country: "",
		timezone: "UTC"
	})
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)
	const [now, setNow] = useState(new Date().toISOString())

	useEffect(() => {
		if (!user) return
		setForm({
			name: user.name || "",
			role: user.role || "",
			company: user.company || "",
			colour: user.colour || "teal",
			country: user.country || "",
			timezone: user.timezone || "UTC"
		})
	}, [user])

	// A live clock is the quickest way to confirm the timezone is the right one.
	useEffect(() => {
		const timer = window.setInterval(() => setNow(new Date().toISOString()), 30000)
		return () => window.clearInterval(timer)
	}, [])

	const countries = config?.countries || []
	const zones = useMemo(
		() => countries.find((country) => country.code === form.country)?.timezones || [],
		[countries, form.country]
	)

	function set(field, value) {
		setForm((current) => ({ ...current, [field]: value }))
	}

	function setCountry(code) {
		const entry = countries.find((country) => country.code === code)
		setForm((current) => ({
			...current,
			country: code,
			timezone: entry?.timezones?.[0] || current.timezone
		}))
	}

	async function save(event) {
		event.preventDefault()
		setBusy(true)
		setError(null)
		try {
			await api.updateMe(form)
			await refreshUser()
			toast("Profile updated", "success")
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<form onSubmit={save} className="space-y-5">
			<Card className="p-5">
				<SectionHeading
					title="How you appear"
					description="Your name, role and company show beside everything you write in a thread."
					className="mb-4"
				/>

				<div className="mb-4 flex items-center gap-3 rounded-lg bg-ink-50 px-3 py-2.5">
					<Avatar person={{ ...user, ...form }} size="lg" />
					<div className="min-w-0">
						<p className="truncate text-[0.9375rem] font-semibold text-ink-900">
							{form.name || user?.email}
						</p>
						<p className="truncate text-2xs text-ink-500">
							{[form.role, form.company].filter(Boolean).join(", ") || user?.email}
						</p>
					</div>
				</div>

				<div className="space-y-3.5">
					<Field label="Full name" error={error}>
						<Input
							value={form.name}
							onChange={(event) => set("name", event.target.value)}
							required
						/>
					</Field>
					<div className="grid gap-3.5 sm:grid-cols-2">
						<Field label="Role">
							<Input
								value={form.role}
								onChange={(event) => set("role", event.target.value)}
								placeholder="Your job title"
							/>
						</Field>
						<Field label="Company">
							<Input
								value={form.company}
								onChange={(event) => set("company", event.target.value)}
								placeholder="Where you work"
							/>
						</Field>
					</div>
					<Field
						label="Email"
						hint="Changing the address you sign in with is not available yet."
					>
						<Input value={user?.email || ""} readOnly disabled />
					</Field>
					<Field
						label="Your colour"
						hint="Used for your avatar and the stripe beside your entries."
					>
						<ColourPicker
							value={form.colour}
							onChange={(value) => set("colour", value)}
							options={ACCENT_KEYS}
						/>
					</Field>
				</div>
			</Card>

			<Card className="p-5">
				<SectionHeading
					title="Timezone"
					className="mb-4"
				/>

				<div className="mb-4 flex items-center gap-2.5 rounded-lg bg-brand-50 px-3 py-2.5 text-[0.8125rem] text-brand-900">
					<Clock size={15} className="shrink-0" aria-hidden />
					<span>
						Right now, for you, it is{" "}
						<strong className="font-semibold">
							{fullTimestamp(now, form.timezone)}
						</strong>
						.
					</span>
				</div>

				<div className="grid gap-3.5 sm:grid-cols-2">
					<Field label="Country">
						<Select
							value={form.country}
							onChange={(event) => setCountry(event.target.value)}
						>
							<option value="">Select your country</option>
							{countries.map((country) => (
								<option key={country.code} value={country.code}>
									{country.name}
								</option>
							))}
						</Select>
					</Field>
					<Field
						label="Timezone"
						hint={
							zones.length > 1
								? "Your country has more than one."
								: "Set from your country."
						}
					>
						<Select
							value={form.timezone}
							onChange={(event) => set("timezone", event.target.value)}
							disabled={zones.length <= 1}
						>
							{zones.length === 0 && (
								<option value={form.timezone}>{zoneLabel(form.timezone)}</option>
							)}
							{zones.map((zone) => (
								<option key={zone} value={zone}>
									{zoneLabel(zone)}
								</option>
							))}
						</Select>
					</Field>
				</div>
			</Card>

			<DisplaySettings />

			<div className="flex justify-end">
				<Button type="submit" variant="primary" icon={Check} busy={busy} disabled={busy}>
					Save changes
				</Button>
			</div>
		</form>
	)
}

/**
 * Applies the moment it is flipped, outside the Save button: it is stored in
 * a cookie on this device, not on the profile.
 */
function DisplaySettings() {
	const { preference, theme, setPreference } = useTheme()
	const dark = theme === "dark"

	return (
		<Card className="p-5">
			<SectionHeading
				title="Display"
				description="Saved on this device. Until you choose, Loomina follows your system setting."
				className="mb-4"
			/>

			<div className="flex items-center justify-between gap-4">
				<div className="flex min-w-0 items-center gap-2.5">
					<Moon size={16} className="shrink-0 text-ink-500" aria-hidden />
					<div className="min-w-0">
						<p id="dark-mode-label" className="text-sm font-medium text-ink-900">
							Dark mode
						</p>
						<p className="text-2xs text-ink-500">
							{preference === "system" ? (
								"Following your system setting."
							) : (
								<>
									Set by you.{" "}
									<button
										type="button"
										onClick={() => setPreference("system")}
										className="font-medium text-brand-700 hover:underline"
									>
										Use system setting
									</button>
								</>
							)}
						</p>
					</div>
				</div>

				<button
					type="button"
					role="switch"
					aria-checked={dark}
					aria-labelledby="dark-mode-label"
					onClick={() => setPreference(dark ? "light" : "dark")}
					className={cx(
						"relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
						dark ? "bg-brand-600" : "bg-ink-300"
					)}
				>
					<span
						className={cx(
							"inline-block h-5 w-5 rounded-full bg-white shadow-card transition-transform",
							dark ? "translate-x-[1.375rem]" : "translate-x-0.5"
						)}
						aria-hidden
					/>
				</button>
			</div>
		</Card>
	)
}
